// Session management utilities
import { Role } from '../types';
import {
  UserPermissions,
  hasPermission as checkPermission,
  canPerformAction as checkAction,
  PermissionLevel,
} from './permissions';
import { GameMode, parseGameMode } from '../utils/seasonType';

export interface UserSession {
  token: string;
  refreshToken?: string; // Add refresh token
  user: {
    id: string;
    email: string;
    name: string;
    role: Role; // Single role object
    permissions?: UserPermissions;
    customPermissions?: Partial<UserPermissions> | null;
  };
  clientId?: string;
  clientName?: string;
  seasonId?: string;
  seasonName?: string;
  gamesMode?: GameMode;
  gameId?: string;
  gameName?: string;
  seasonGameFilter?: {
    mode: 'all' | 'league' | 'custom' | 'single';
    gameId?: string;
    customGameIds?: string[];
  };
  activeQuarter?: string;
  isAuthenticated: boolean;
  startDate?: string;
  endDate?: string;
  lastValidated?: number; // Timestamp of last validation
}

const SESSION_KEY = 'statdog_session';

// Helper function to dispatch session change event
const dispatchSessionChangeEvent = () => {
  window.dispatchEvent(new CustomEvent('sessionChange'));
};

export const sessionManager = {
  // Save session data to localStorage
  saveSession: (session: UserSession): void => {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('Error saving session:', error);
    }
  },

  // Get session data from localStorage
  getSession: (): UserSession | null => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (sessionData) {
        const session = JSON.parse(sessionData);
        // Check if session is still valid (you can add expiration logic here)
        return session;
      }
    } catch (error) {
      console.error('Error reading session:', error);
    }
    return null;
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const session = sessionManager.getSession();
    return session?.isAuthenticated === true && !!session?.token;
  },

  // Get authentication token
  getToken: (): string | null => {
    const session = sessionManager.getSession();
    return session?.token || null;
  },

  // Get refresh token
  getRefreshToken: (): string | null => {
    const session = sessionManager.getSession();
    return session?.refreshToken || null;
  },

  // Update just the access token (used after refresh)
  updateToken: (newToken: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = {
        ...currentSession,
        token: newToken,
        lastValidated: Date.now(),
      };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Get user data
  getUser: () => {
    const session = sessionManager.getSession();
    return session?.user || null;
  },

  // Get user role label
  getUserRoleLabel: (): string | null => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    // Return the role label, or null if no role
    return role?.label || null;
  },

  // Check if user is employee
  isEmployee: (): boolean => {
    const session = sessionManager.getSession();
    const employeeRoles = ['owner', 'customerSupport', 'salesMarketing', 'softwareEngineer'];
    const role = session?.user?.role;
    return role ? employeeRoles.includes(role.value) : false;
  },

  // Check if user is admin (highest employee role)
  isAdmin: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'owner';
  },

  // Check if user is customer support
  isCustomerSupport: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'customerSupport';
  },

  // Check if user is sales and marketing
  isSalesMarketing: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'salesMarketing';
  },

  // Check if user is software engineer
  isSoftwareEngineer: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'softwareEngineer';
  },

  // Check if user can manage employees (Owner or Admin)
  canManageEmployees: (): boolean => {
    return sessionManager.isAdmin();
  },

  // Check if user can manage all clients (Owner, Admin, Customer Support)
  canManageAllClients: (): boolean => {
    return sessionManager.isAdmin() || sessionManager.isCustomerSupport();
  },

  // Check if user can delete clients (Owner, Admin, Customer Support - excludes Sales and Marketing)
  canDeleteClients: (): boolean => {
    return sessionManager.isAdmin() || sessionManager.isCustomerSupport();
  },

  // Check if user has specific role
  hasRole: (roleValue: string): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === roleValue;
  },

  // Check if user is head coach
  isHeadCoach: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'headCoach';
  },

  // Check if user is assistant coach
  isAssistantCoach: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'assistantCoach';
  },

  // Check if user is any type of coach
  isCoach: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'headCoach' || role?.value === 'assistantCoach';
  },

  // Check if user is stat manager
  isStatManager: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'statManager';
  },

  // Check if user is stat taker
  isStatTaker: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'statTaker';
  },

  // Get client role (for role hierarchy checks)
  getClientRole: (): { value: string; label: string } | null => {
    const session = sessionManager.getSession();
    return session?.user?.role || null;
  },

  // Check if user is player
  isPlayer: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'player';
  },

  // Check if user is fan
  isFan: (): boolean => {
    const session = sessionManager.getSession();
    const role = session?.user?.role;
    return role?.value === 'parent';
  },

  // Get user role (single role object)
  getUserRole: (): Role | null => {
    const session = sessionManager.getSession();
    return session?.user?.role || null;
  },

  // Get user permissions
  getUserPermissions: (): UserPermissions | null => {
    const session = sessionManager.getSession();
    return session?.user?.permissions || null;
  },

  // Get user custom permissions
  getUserCustomPermissions: (): Partial<UserPermissions> | null => {
    const session = sessionManager.getSession();
    return session?.user?.customPermissions || null;
  },

  // Check if user has permission for a resource
  hasPermission: (resource: string, requiredLevel: PermissionLevel = PermissionLevel.VIEW): boolean => {
    // Employees have full access
    if (sessionManager.isEmployee()) {
      return true;
    }

    const permissions = sessionManager.getUserPermissions();
    return checkPermission(permissions, resource, requiredLevel);
  },

  // Check if user can perform specific action
  canPerformAction: (resource: string, action: 'view' | 'add' | 'edit' | 'delete'): boolean => {
    // Employees have full access
    if (sessionManager.isEmployee()) {
      return true;
    }

    const permissions = sessionManager.getUserPermissions();
    return checkAction(permissions, resource, action);
  },

  // Check if user can access data sharing features
  canAccessDataSharing: (): boolean => {
    // Employees have full access
    if (sessionManager.isEmployee()) {
      return true;
    }

    const permissions = sessionManager.getUserPermissions();
    return permissions?.dataSharing === true;
  },

  canAccessPracticeGames: (): boolean => {
    if (sessionManager.isEmployee()) {
      return true;
    }

    const permissions = sessionManager.getUserPermissions();
    if (permissions?.games?.practiceGames === true) return true;
    if (permissions?.games?.practiceGames === false) return false;
    return sessionManager.isCoach();
  },

  // Get client ID from session
  getClientId: (): string | null => {
    const session = sessionManager.getSession();
    return session?.clientId || null;
  },

  // Set client ID in session
  setClientId: (clientId: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = { ...currentSession, clientId };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Get client name from session
  getClientName: (): string | null => {
    const session = sessionManager.getSession();
    return session?.clientName || null;
  },

  // Set client name in session
  setClientName: (clientName: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = { ...currentSession, clientName };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Get season ID from session
  getSeasonId: (): string | null => {
    const session = sessionManager.getSession();
    return session?.seasonId || null;
  },

  // Set season ID in session
  setSeasonId: (seasonId: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      // Clear game info when switching seasons
      const updatedSession = {
        ...currentSession,
        seasonId,
        gameId: undefined,
        gameName: undefined,
        seasonGameFilter: undefined,
        activeQuarter: undefined,
      };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Get season name from session
  getSeasonName: (): string | null => {
    const session = sessionManager.getSession();
    return session?.seasonName || null;
  },

  // Set season name in session
  setSeasonName: (seasonName: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = { ...currentSession, seasonName };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Regular vs Practice (Intra Squad) game list mode
  getGamesMode: (): GameMode => {
    const session = sessionManager.getSession();
    return parseGameMode(session?.gamesMode);
  },

  setGamesMode: (gamesMode: GameMode): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = { ...currentSession, gamesMode };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Get game ID from session
  getGameId: (): string | null => {
    const session = sessionManager.getSession();
    return session?.gameId || null;
  },

  // Set game ID in session
  setGameId: (gameId: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      // Clear activeQuarter when switching games
      const updatedSession = {
        ...currentSession,
        gameId,
        activeQuarter: undefined,
      };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Get game name from session
  getGameName: (): string | null => {
    const session = sessionManager.getSession();
    return session?.gameName || null;
  },

  // Set game name in session
  setGameName: (gameName: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = { ...currentSession, gameName };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Get season games filter selection from session
  getSeasonGameFilter: ():
    | {
        mode: 'all' | 'league' | 'custom' | 'single';
        gameId?: string;
        customGameIds?: string[];
      }
    | null => {
    const session = sessionManager.getSession();
    return session?.seasonGameFilter || null;
  },

  // Set season games filter selection in session
  setSeasonGameFilter: (filter: {
    mode: 'all' | 'league' | 'custom' | 'single';
    gameId?: string;
    customGameIds?: string[];
  }): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = {
        ...currentSession,
        seasonGameFilter: {
          mode: filter.mode,
          gameId: filter.gameId,
          customGameIds: filter.customGameIds || [],
        },
      };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Get active quarter from session
  getActiveQuarter: (): string | null => {
    const session = sessionManager.getSession();
    return session?.activeQuarter || null;
  },

  // Set active quarter in session
  setActiveQuarter: (activeQuarter: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = { ...currentSession, activeQuarter };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Get start date from session
  getStartDate: (): string | null => {
    const session = sessionManager.getSession();
    return session?.startDate || null;
  },

  // Set start date in session
  setStartDate: (startDate: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = { ...currentSession, startDate };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Get end date from session
  getEndDate: (): string | null => {
    const session = sessionManager.getSession();
    return session?.endDate || null;
  },

  // Set end date in session
  setEndDate: (endDate: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = { ...currentSession, endDate };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Clear session (logout)
  clearSession: (): void => {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch (error) {
      console.error('Error clearing session:', error);
    }
  },

  // Update session data
  updateSession: (updates: Partial<UserSession>): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = { ...currentSession, ...updates };
      sessionManager.saveSession(updatedSession);
    }
  },

  // Update user permissions (used after refresh or validation)
  updateUserPermissions: (permissions: UserPermissions, customPermissions: Partial<UserPermissions> | null): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const updatedSession = {
        ...currentSession,
        user: {
          ...currentSession.user,
          permissions,
          customPermissions,
        },
        lastValidated: Date.now(),
      };
      sessionManager.saveSession(updatedSession);
      dispatchSessionChangeEvent();
    }
  },

  // Check if session needs validation (last validated > 5 minutes ago)
  needsValidation: (): boolean => {
    const session = sessionManager.getSession();
    if (!session || !session.lastValidated) return true;

    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    return session.lastValidated < fiveMinutesAgo;
  },

  // Clear client-related data (season, game, dates) while setting new client info
  clearClientRelatedData: (clientId: string, clientName: string): void => {
    const currentSession = sessionManager.getSession();
    if (currentSession) {
      const clearedSession = {
        ...currentSession,
        clientId,
        clientName,
        seasonId: undefined,
        seasonName: undefined,
        gamesMode: undefined,
        gameId: undefined,
        gameName: undefined,
        seasonGameFilter: undefined,
        activeQuarter: undefined,
        startDate: undefined,
        endDate: undefined,
      };
      sessionManager.saveSession(clearedSession);
      dispatchSessionChangeEvent();
    }
  },
};
